// DataMeridian Consulting — factual content (no fabricated stats, testimonials, awards)

export const COMPANY = {
  name: "DataMeridian Consulting",
  tagline: "Enterprise Database | Cloud | Data Engineering",
  brandLine: "Aligning Data. Empowering Decisions.",
  footerTagline: "Remote Consulting Worldwide",
  email: "contact@datameridianconsulting.com",
  yearsExperience: "12+ years",
  positioning:
    "A boutique enterprise technology consulting company helping organizations design, modernize, optimize and support mission-critical database environments.",
  about:
    "DataMeridian Consulting is a boutique technology consulting company helping organizations design, modernize, optimize and support mission-critical database environments. Our consulting expertise has been developed through more than 12 years of working on enterprise database environments supporting multinational organizations across manufacturing, logistics, supply chain and technology industries. We help organizations modernize legacy database platforms, optimize database performance, migrate workloads to cloud platforms and build reliable production database environments. Every engagement focuses on measurable business outcomes, long-term reliability, operational excellence and scalable architecture.",
};

export const CONTACT_INFO = {
  email: "contact@datameridianconsulting.com",
  phonePrimary: "+91 98336 24208",
  phoneAlternate: "+91 84518 38752",
  availabilityDays: "Monday – Friday",
  availabilityHours: "9:00 AM – 7:00 PM IST",
  availabilityNote: "Global Remote Consulting Available",
};

// International-format tel: hrefs (strip spaces)
export const telHref = (num) => `tel:${num.replace(/\s+/g, "")}`;

// Factual capability descriptors — no numbers, no fake counts
export const CAPABILITIES = [
  { label: "Enterprise Databases", detail: "SQL Server · PostgreSQL" },
  { label: "Cloud Modernization",  detail: "Azure SQL · Managed Instance" },
  { label: "Data Engineering",     detail: "ETL · SSIS · Automation" },
  { label: "Global Delivery",      detail: "Remote Consulting Worldwide" },
];

export const REGIONS = [
  "United States", "Canada", "United Kingdom", "Ireland", "Germany",
  "Netherlands", "France", "Switzerland", "Sweden", "Australia",
  "New Zealand", "Singapore", "United Arab Emirates", "Saudi Arabia",
  "Qatar", "Oman", "Bahrain", "India",
];

// Industries the practice has served (no employer or client names)
export const ENTERPRISE_INDUSTRIES = [
  "Manufacturing", "Logistics", "Supply Chain", "Retail",
  "Technology", "Healthcare", "Financial Services", "Software as a Service (SaaS)",
];

export const EXPERTISE_AREAS = [
  "Enterprise SQL Server Administration",
  "PostgreSQL Administration",
  "Azure SQL Managed Instance",
  "Azure SQL Database",
  "Postgres to SQL Server MI Migration",
  "Database Performance Engineering",
  "Query Optimization",
  "Cloud Database Migration",
  "SQL Server to PostgreSQL Migration",
  "Cloud Architecture Design",
  "Cloud Cost Optimization",
  "Database Security",
  "High Availability",
  "Always On Availability Groups",
  "Disaster Recovery",
  "Backup & Recovery",
  "ETL Engineering",
  "Database Automation",
  "Production Support",
  "Capacity Planning",
  "Database Monitoring",
];

export const SERVICE_CATEGORIES = [
  { id: "databases",   name: "Database Engineering" },
  { id: "cloud",       name: "Cloud & Migration" },
  { id: "reliability", name: "Reliability & HA/DR" },
  { id: "data",        name: "Data Engineering" },
  { id: "managed",     name: "Managed Services" },
];

// Services — no pricing, no fabricated metrics
export const SERVICES = [
  { slug: "enterprise-sql-server-consulting", category: "databases", name: "Enterprise SQL Server Consulting",
    tagline: "Architecture, performance and modernization for Microsoft SQL Server.",
    summary: "End-to-end consulting for enterprise SQL Server environments — from schema and index design to workload tuning, HA/DR architecture and cloud modernization.",
    icon: "Database" },
  { slug: "postgresql-consulting", category: "databases", name: "PostgreSQL Consulting",
    tagline: "PostgreSQL platform design, tuning and operations.",
    summary: "Deep PostgreSQL expertise covering vacuum strategy, partitioning, replication, extensions and production operations.",
    icon: "Database" },
  { slug: "azure-sql-database", category: "cloud", name: "Azure SQL Database",
    tagline: "Design and operate Azure SQL for elastic enterprise workloads.",
    summary: "Right-sizing, tiering, private endpoints, elastic pools and integration with Azure Monitor and Azure security controls.",
    icon: "Cloud" },
  { slug: "azure-sql-managed-instance", category: "cloud", name: "Azure SQL Managed Instance",
    tagline: "Lift-and-shift with near 100% SQL Server compatibility.",
    summary: "Instance sizing, VNET integration, business-continuity design and full assessment for Managed Instance migration.",
    icon: "Cloud" },
  { slug: "database-performance-engineering", category: "databases", name: "Database Performance Engineering",
    tagline: "Systematic performance engineering, not one-off tuning.",
    summary: "Wait-stat analysis, index and statistics strategy, plan-regression fixes, memory and I/O tuning across enterprise workloads.",
    icon: "Gauge" },
  { slug: "database-health-assessment", category: "databases", name: "Database Health Assessment",
    tagline: "A 360° assessment across performance, security and DR.",
    summary: "Structured assessment delivering a scored report and a prioritised remediation roadmap for the environment.",
    icon: "Stethoscope" },
  { slug: "query-optimization", category: "databases", name: "Query Optimization",
    tagline: "Rewrite, refactor and re-plan expensive queries.",
    summary: "Execution-plan reviews, adaptive-query features, parameterization strategy and cardinality-estimation fixes.",
    icon: "Search" },
  { slug: "cloud-database-migration", category: "cloud", name: "Cloud Database Migration",
    tagline: "Move workloads to Azure with predictable outcomes.",
    summary: "Assessment, proof-of-concept, cut-over rehearsal and rollback playbooks with measurable success criteria.",
    icon: "MoveRight" },
  { slug: "sql-server-to-postgresql-migration", category: "cloud", name: "SQL Server to PostgreSQL Migration",
    tagline: "Reduce licensing without disrupting the business.",
    summary: "Schema conversion, T-SQL to PL/pgSQL rewrites, dual-write patterns and automated regression testing.",
    icon: "GitBranch" },
  { slug: "database-modernization", category: "cloud", name: "Database Modernization",
    tagline: "Modernize legacy databases into fit-for-purpose engines.",
    summary: "Refactor legacy databases and processes onto modern platforms with a measurable business-outcome model.",
    icon: "Rocket" },
  { slug: "database-security-assessment", category: "reliability", name: "Database Security Assessment",
    tagline: "Identify gaps before they become incidents.",
    summary: "Standards-aligned reviews covering encryption, TDE, Always Encrypted, RLS, auditing and privileged-access hardening.",
    icon: "Lock" },
  { slug: "always-on-availability-groups", category: "reliability", name: "Always On Availability Groups",
    tagline: "Correctly designed and operated Always On.",
    summary: "Availability Group topology, distributed AGs, read-scale routing, latency tuning and DR drills.",
    icon: "Network" },
  { slug: "high-availability", category: "reliability", name: "High Availability",
    tagline: "Design and operate HA architectures that hold up under load.",
    summary: "HA topologies across SQL Server and PostgreSQL, tuned to real RPO/RTO objectives.",
    icon: "ShieldCheck" },
  { slug: "disaster-recovery", category: "reliability", name: "Disaster Recovery",
    tagline: "Design for the outages you hope never happen.",
    summary: "RPO/RTO analysis, multi-region topologies, failover automation and DR tabletop exercises.",
    icon: "LifeBuoy" },
  { slug: "backup-and-recovery", category: "reliability", name: "Backup & Recovery",
    tagline: "Recoverable backups, validated regularly.",
    summary: "Point-in-time strategies, cross-region copies, immutability, and continuous restore validation.",
    icon: "Save" },
  { slug: "managed-dba-services", category: "managed", name: "Managed DBA Services",
    tagline: "Named senior DBAs, tailored to your stack.",
    summary: "SLA-backed operations with named engineers, runbooks and periodic service reviews.",
    icon: "Headphones" },
  { slug: "production-database-support", category: "managed", name: "Production Database Support",
    tagline: "Engineering-grade support, not ticket triage.",
    summary: "Incident response, root-cause analysis and permanent-fix engineering for production database environments.",
    icon: "Users" },
  { slug: "database-monitoring", category: "managed", name: "Database Monitoring",
    tagline: "Meaningful monitoring, not alert fatigue.",
    summary: "Monitoring architecture with predictive alerts, correlated diagnostics and executive reporting.",
    icon: "Radar" },
  { slug: "capacity-planning", category: "reliability", name: "Capacity Planning",
    tagline: "Right-size today. Scale confidently tomorrow.",
    summary: "Workload forecasting, cost modelling and infrastructure sizing across on-prem and cloud.",
    icon: "TrendingUp" },
  { slug: "ssis-development", category: "data", name: "SSIS Development",
    tagline: "Modern, maintainable SSIS packages.",
    summary: "Framework-driven SSIS with configuration, logging, and Azure Data Factory-ready patterns.",
    icon: "Boxes" },
  { slug: "etl-engineering", category: "data", name: "ETL Engineering",
    tagline: "Reliable pipelines across on-prem and cloud.",
    summary: "Batch and streaming ETL/ELT engineered for idempotency, replay and lineage.",
    icon: "Workflow" },
  { slug: "data-engineering", category: "data", name: "Data Engineering",
    tagline: "Data platforms teams can trust.",
    summary: "Ingestion, transformation, orchestration and governance across enterprise data platforms.",
    icon: "Layers" },
  { slug: "postgres-to-sql-server-mi-migration", category: "cloud", name: "Postgres to SQL Server MI Migration",
    tagline: "End-to-end migration from PostgreSQL to Azure SQL Managed Instance.",
    summary: "Full-lifecycle migration engagement: assessment, schema and code conversion, data-movement design, dual-write patterns, DR posture and cut-over rehearsals — engineered for zero-surprise go-live.",
    icon: "GitBranch" },
  { slug: "cloud-architecture-design", category: "cloud", name: "Cloud Architecture Design",
    tagline: "Decide the right architecture on cloud for your organization.",
    summary: "Reference architectures for enterprise data platforms on Azure, AWS or GCP — with clear trade-offs on cost, reliability, security and operational complexity, mapped to your business goals.",
    icon: "Compass" },
  { slug: "cloud-cost-optimization", category: "cloud", name: "Cloud Cost Optimization",
    tagline: "Reduce cloud database spend without hurting performance.",
    summary: "Workload-level cost analysis, right-sizing, tier and reservation strategy, storage & backup optimisation, and governance guardrails so the savings stick after the engagement ends.",
    icon: "TrendingDown" },
];

export const INDUSTRIES = [
  { slug: "manufacturing", name: "Manufacturing",
    summary: "MES and IoT data pipelines feeding predictive maintenance and OEE reporting.", icon: "Factory" },
  { slug: "logistics",     name: "Logistics",
    summary: "Real-time visibility across warehouses, fleets and cross-border movements.", icon: "Truck" },
  { slug: "supply-chain",  name: "Supply Chain",
    summary: "Reliable data platforms for global supply chain planning and execution.", icon: "PackageSearch" },
  { slug: "retail",        name: "Retail",
    summary: "Unified customer, inventory and demand data for omnichannel operations.", icon: "ShoppingBag" },
  { slug: "technology",    name: "Technology",
    summary: "Database platforms behind technology products, at scale.", icon: "Cpu" },
  { slug: "healthcare",    name: "Healthcare",
    summary: "Compliance-aligned databases for clinical, claims and analytics workloads.", icon: "HeartPulse" },
  { slug: "financial-services", name: "Financial Services",
    summary: "Low-latency, audit-ready platforms for banking, payments and lending.", icon: "Banknote" },
  { slug: "saas",          name: "Software as a Service (SaaS)",
    summary: "Multi-tenant databases, per-tenant SLAs and safe schema evolution.", icon: "Cloud" },
];

export const TECHNOLOGIES = [
  { name: "Microsoft SQL Server",        group: "Databases" },
  { name: "PostgreSQL",                  group: "Databases" },
  { name: "Azure SQL Database",          group: "Cloud" },
  { name: "Azure SQL Managed Instance",  group: "Cloud" },
  { name: "Azure Data Factory",          group: "Cloud" },
  { name: "SQL Server Integration Services (SSIS)", group: "Data Integration" },
  { name: "T-SQL",                       group: "Languages" },
  { name: "PowerShell",                  group: "Automation" },
  { name: "Performance Monitoring",      group: "Operations" },
  { name: "Database Automation",         group: "Operations" },
  { name: "Always On Availability Groups", group: "High Availability" },
];

export const WHY_CHOOSE = [
  { title: "Enterprise Database Expertise",         body: "Consulting focused on enterprise database environments — the platforms our engineers have specialised in for over a decade." },
  { title: "Production Environment Experience",     body: "Every recommendation is grounded in production reality, not theoretical best practice." },
  { title: "Performance Engineering Specialists",   body: "A structured engineering discipline for wait-stats, indexing, planning and workload tuning." },
  { title: "Cloud Database Modernization",          body: "Deep experience modernising database estates onto Azure SQL and Managed Instance." },
  { title: "Security First Approach",               body: "Encryption, access hardening, auditing and posture baked into every engagement." },
  { title: "Reliable Delivery Methodology",         body: "A repeatable delivery model that clients can plan around with confidence." },
  { title: "Global Remote Delivery",                body: "Remote consulting delivered across the Americas, EMEA and APAC." },
  { title: "Tailored Consulting Engagements",       body: "Every engagement is scoped to your infrastructure, business goals and constraints." },
  { title: "Long-Term Technology Partnership",      body: "Relationships built for the long term, not one-off transactions." },
  { title: "Business Outcome Driven Consulting",    body: "Success measured in throughput, cost, RPO/RTO and business outcomes — not activity." },
];

export const DELIVERY_MODEL = [
  { step: "01", title: "Discovery",             body: "Structured discovery to understand your environment, goals and constraints." },
  { step: "02", title: "Technical Assessment",  body: "Deep technical review of the database estate, performance, security and DR posture." },
  { step: "03", title: "Architecture Review",   body: "Reference-architecture review measured against your business requirements." },
  { step: "04", title: "Solution Design",       body: "A written design with measurable success criteria — throughput, cost, RPO/RTO." },
  { step: "05", title: "Implementation",        body: "Delivered in sprints by senior specialists with continuous progress reporting." },
  { step: "06", title: "Knowledge Transfer",    body: "Structured handover with runbooks, documentation and stakeholder enablement." },
  { step: "07", title: "Continuous Support",    body: "Optional continuous support to keep the environment healthy over time." },
];

// Case studies — kept as anonymised, industry-based stories (no client or employer names)
export const CASE_STUDIES = [
  { slug: "manufacturing-database-modernization",
    title: "Database modernization for a global manufacturer",
    industry: "Manufacturing",
    summary:
      "Consolidated a fragmented SQL Server estate and modernised the target platform onto Azure SQL Managed Instance.",
    image: "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=1600&q=70",
    challenge:
      "A fragmented database estate across factories was slowing enterprise reporting and increasing operational risk.",
    approach: [
      "Discovery across the environment and stakeholder interviews",
      "Assessment for Azure SQL Managed Instance suitability",
      "Consolidation plan aligned to business continuity requirements",
      "Cut-over rehearsals and DR validation",
    ],
    outcome: [
      "A consolidated, modern database platform",
      "Improved availability posture",
      "Simplified operations and reporting",
    ],
  },
  { slug: "logistics-performance-engineering",
    title: "Performance engineering for a logistics platform",
    industry: "Logistics",
    summary:
      "Systematic performance engineering across a high-throughput SQL Server workload supporting operational reporting.",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=70",
    challenge:
      "Peak-hour reporting was straining the operational database and impacting user experience.",
    approach: [
      "Wait-statistics baseline and workload characterisation",
      "Redesigned indexing and statistics strategy on hot tables",
      "Stored-procedure refactor for parameter-sensitive plans",
      "Read-scale patterns to isolate reporting traffic",
    ],
    outcome: [
      "Significant reduction in peak-hour latency",
      "Reduced locking and blocking incidents",
      "Predictable performance during peak windows",
    ],
  },
  { slug: "saas-postgres-migration",
    title: "SQL Server to PostgreSQL migration for a SaaS platform",
    industry: "Software as a Service (SaaS)",
    summary:
      "Structured migration from SQL Server to PostgreSQL for a multi-tenant SaaS platform with zero customer-visible downtime.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=70",
    challenge:
      "Rising licensing costs and a strategic requirement to move to an open-source database platform.",
    approach: [
      "Automated schema and code conversion",
      "Dual-write patterns for a safe cut-over",
      "Row-level security aligned to tenants",
      "Regression testing across critical workloads",
    ],
    outcome: [
      "Successful cut-over with zero customer-visible downtime",
      "Reduced database licensing footprint",
      "Modern operational model on PostgreSQL",
    ],
  },
];

// Blog / insights — technical field notes (no employer or client references)
export const BLOG_POSTS = [
  { slug: "sql-server-features-worth-adopting",
    title: "SQL Server: the features worth adopting in production",
    excerpt: "A field-tested view of Parameter Sensitive Plan optimisation, Query Store hints and where each one earns its place.",
    author: "DataMeridian Consulting", date: "2025-11-12", readTime: "9 min", tag: "SQL Server",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=70",
    body: "Modern SQL Server releases have shipped a set of features that quietly change how we tune production workloads. In this note we walk through the features we most commonly recommend to enterprise clients — Parameter Sensitive Plan optimisation, Query Store hints and workload isolation patterns — and where each one earns its place. The short version: adopt Parameter Sensitive Plan optimisation early, treat Query Store hints as a scalpel rather than a hammer, and always measure with a workload baseline before and after." },
  { slug: "postgres-vs-sql-server",
    title: "Postgres or SQL Server: a decision framework",
    excerpt: "A structured framework we use with clients before a single line of code is written.",
    author: "DataMeridian Consulting", date: "2025-10-20", readTime: "11 min", tag: "PostgreSQL",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=1600&q=70",
    body: "Migration decisions that skip a structured framework almost always over-run. We share ours in this note: a scorecard covering licensing pressure, operational maturity, feature dependency and total cost of change. We also cover three anti-patterns that regularly hurt migrations: naïve schema translation, missing dual-write phase, and no performance baseline. Use the checklist before committing to a direction." },
  { slug: "always-on-availability-groups-pitfalls",
    title: "Common pitfalls we see in Always On deployments",
    excerpt: "The read-replica traps, quorum surprises and DR drills teams skip until it's too late.",
    author: "DataMeridian Consulting", date: "2025-09-30", readTime: "7 min", tag: "High Availability",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=70",
    body: "Always On Availability Groups remain among the most commonly misunderstood high-availability technologies in the SQL Server world. In this note we walk through five failure patterns we routinely encounter — wrong quorum model for the topology, read-replica staleness treated as scaling strategy, missing tabletop DR drills, no automated failover validation and unmanaged distributed AGs — and how each is best avoided." },
  { slug: "azure-sql-managed-instance-cost-tuning",
    title: "Cost tuning for Azure SQL Managed Instance",
    excerpt: "A practical guide to right-sizing MI, aligning storage and reclaiming licence value.",
    author: "DataMeridian Consulting", date: "2025-09-04", readTime: "8 min", tag: "Azure",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=70",
    body: "Managed Instance can quietly become one of the larger line items on an Azure invoice. This piece walks through a five-step cost-tuning workflow: workload segmentation, right-sizing by peak-p95 not peak-max, storage tier alignment, backup retention review and Azure Hybrid Benefit verification. Applied together, these steps typically reduce cost meaningfully within a single billing cycle." },
  { slug: "database-security-modern-baseline",
    title: "A modern baseline for enterprise database security",
    excerpt: "Encryption at rest is table stakes — here is what a modern posture actually looks like.",
    author: "DataMeridian Consulting", date: "2025-07-22", readTime: "9 min", tag: "Security",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1600&q=70",
    body: "In this note we outline the security posture we now recommend for enterprise database environments: Always Encrypted with secure enclaves, dynamic data masking, row-level security tied to identity, privileged-access workflows and continuous audit into a SIEM. If your database security stops at encryption at rest, there is meaningful work to do." },
  { slug: "devops-for-databases",
    title: "DevOps for databases — safely",
    excerpt: "How to bring schema changes under CI/CD without turning production into a lottery.",
    author: "DataMeridian Consulting", date: "2025-05-08", readTime: "8 min", tag: "DevOps",
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1600&q=70",
    body: "Database CI/CD is where many DevOps programmes quietly stall. In this note we contrast state-based and migration-based approaches, share the pipeline pattern we use across most engagements and cover the guardrails that keep the blast radius under control — policy gates, automated rollback and safe online DDL patterns." },
];

export const CAREERS = [
  { slug: "senior-sql-server-consultant",  title: "Senior SQL Server Consultant",     location: "Remote — Global", type: "Full-time", team: "Consulting" },
  { slug: "principal-postgres-engineer",   title: "Principal PostgreSQL Engineer",    location: "Remote — Global", type: "Full-time", team: "Consulting" },
  { slug: "cloud-migration-lead-azure",    title: "Cloud Migration Lead — Azure",     location: "Remote — Global", type: "Full-time", team: "Cloud" },
  { slug: "data-engineer",                 title: "Data Engineer",                    location: "Remote — Global", type: "Full-time", team: "Data Engineering" },
  { slug: "managed-services-dba",          title: "Managed Services DBA",             location: "Remote — Global", type: "Full-time", team: "Managed Services" },
];

export const HERO_IMAGES = {
  main:   "https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg",
  about:  "https://images.pexels.com/photos/36765718/pexels-photo-36765718.jpeg",
  global: "https://images.pexels.com/photos/7411925/pexels-photo-7411925.jpeg",
  data:   "https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg",
};

export const findService = (slug) => SERVICES.find((s) => s.slug === slug);
export const findIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);
export const findCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
export const findPost = (slug) => BLOG_POSTS.find((b) => b.slug === slug);
